import { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { Checkbox } from '@wartek-id/checkbox-v1'
import type { DialogProps } from '@wartek-id/dialog-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import { useAPIPostPelaksanaanAppealApproval } from 'api/kinerja/useAPIKinerja'
import { API_PATH_NOTIFICATION_LIST } from 'api/kinerja/useAPIKinerjaNotification'
import { PENGELOLAAN_KINERJA_PELAKSANAAN_STEPS } from 'app/KinerjaShared/constants/pelaksanaanSteps'
import { assetUrl } from 'utils/url'

import type { TPelaksanaanAppealableStep } from 'types/kinerja-pelaksanaan'

type DialogPelaksanaanAppealRejectionProps = Omit<DialogProps, 'children'> & {
  guruName: string
  requestId?: string // identifier for rollback request
  step: TPelaksanaanAppealableStep
}

export function DialogPelaksanaanAppealRejection({
  guruName,
  requestId,
  isOpen,
  step,
  onClose,
  ...dialogProps
}: DialogPelaksanaanAppealRejectionProps) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [isConfirmed, setEnableIsConfirmed] = useState(false)
  const [activeContent, setActiveContent] = useState<'FORM' | 'SUCCESS'>('FORM')

  const rollbackRequestMutation = useAPIPostPelaksanaanAppealApproval(requestId)

  const handleSubmit = () => {
    rollbackRequestMutation.mutate(
      { confirmationState: 'REJECTED' },
      {
        onSuccess: () => {
          setActiveContent('SUCCESS')
          queryClient.invalidateQueries(API_PATH_NOTIFICATION_LIST)
        },
        onError: () => {
          onClose()
          toast({ message: 'Gagal lanjut. Coba lagi.' })
        },
      }
    )
  }

  // Reset dialog upon dismissal
  useEffect(() => {
    if (!isOpen) {
      setEnableIsConfirmed(false)
      setActiveContent('FORM')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const formName =
    PENGELOLAAN_KINERJA_PELAKSANAAN_STEPS[step]?.title ?? 'dokumen'
  const isLoadingSubmission = rollbackRequestMutation.isLoading

  return (
    <Dialog
      closeOnOverlayClick={false}
      isOpen={isOpen}
      size="sm"
      {...dialogProps}
    >
      <DialogOverlay />
      <DialogContent className="p-6">
        {activeContent === 'FORM' && (
          <>
            <Icon
              color="critical"
              className="!text-5xl mb-2"
              width={48}
              height={48}
            >
              warning
            </Icon>
            <Text variant="display-sm" className="mb-2">
              {`Tolak pengajuan perubahan ${formName} milik ${guruName}?`}
            </Text>
            <Text color="subdued" variant="body-lg" className="mb-4">
              Status pengajuan ini akan ditampilkan ke guru dan guru dapat
              mengulangi pengajuan perubahan.
            </Text>
            <Checkbox
              checked={isConfirmed}
              className="mb-4"
              labelPosition="center"
              onChange={() => setEnableIsConfirmed(!isConfirmed)}
            >
              Saya mengerti bahwa guru dapat mengulangi pengajuan perubahan.
            </Checkbox>
            <div className="flex space-x-5">
              <Button
                color="white"
                size="lg"
                disabled={isLoadingSubmission}
                onClick={() => onClose?.()}
              >
                Kembali
              </Button>
              <Button
                color="blue"
                size="lg"
                isLoading={isLoadingSubmission}
                disabled={isLoadingSubmission || !isConfirmed}
                onClick={() => handleSubmit()}
              >
                Lanjut tolak
              </Button>
            </div>
          </>
        )}

        {activeContent === 'SUCCESS' && (
          <>
            <img
              src={assetUrl('/illustrations/img-files-document-failed.png')}
              width={84}
              height={84}
              alt=""
              className="mb-6"
            />
            <Text variant="display-sm" className="mb-4">
              Pengajuan perubahan ditolak
            </Text>
            <Text variant="body-lg" color="subdued" className="mb-6">
              Anda tidak perlu mengulang penilaian.
            </Text>
            <Button size="lg" onClick={() => onClose?.()}>
              Saya mengerti
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
